import { useEffect } from 'react';
import { useOnboardingTour, TOUR_STEPS } from '../../contexts/OnboardingTourContext';
import { useSystemSettings } from '../../hooks/useSystemSettings';

/**
 * OnboardingTour — Product Tour overlay.
 * Renders a semi-transparent backdrop + a step card.
 * Uses the app primary color from system settings.
 */
export const OnboardingTour = () => {
  const { tourActive, currentStep, totalSteps, step, nextStep, prevStep, skipTour } = useOnboardingTour();
  const { settings } = useSystemSettings();
  const appPrimary = settings.app_primary_color || '#10b981';

  // Prevent scroll while tour is active
  useEffect(() => {
    if (tourActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [tourActive]);

  if (!tourActive) return null;

  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;
  const progressPct = ((currentStep + 1) / totalSteps) * 100;

  // Position the card based on targetHint
  const getCardPosition = () => {
    switch (step.targetHint) {
      case 'sidebar':
        return 'lg:left-[300px] lg:top-1/2 lg:-translate-y-1/2 left-4 top-1/2 -translate-y-1/2';
      case 'header':
        return 'top-[90px] left-1/2 -translate-x-1/2';
      case 'fab':
        return 'bottom-24 right-6 lg:right-8';
      case 'main':
        return 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';
      case 'center':
      default:
        return 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/55 backdrop-blur-[2px]"
        onClick={skipTour}
      />

      {/* Step Card */}
      <div
        className={`fixed z-[9999] w-[min(420px,calc(100vw-2rem))] ${getCardPosition()}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden select-none">
          {/* Top color bar */}
          <div
            className="h-1.5 w-full"
            style={{ background: `linear-gradient(90deg, ${appPrimary} ${progressPct}%, #e5e7eb ${progressPct}%)` }}
          />

          {/* Header */}
          <div className="px-6 pt-5 pb-0 flex items-start justify-between gap-3">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${appPrimary}18` }}
            >
              <i
                className={`${step.icon} text-2xl`}
                style={{ color: appPrimary }}
              />
            </div>
            <button
              onClick={skipTour}
              className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer shrink-0 mt-0.5"
              title="Fechar tour"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4">
            {/* Step counter */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-700 text-gray-400 uppercase tracking-wide">
                Passo {currentStep + 1} de {totalSteps}
              </span>
            </div>

            <h3 className="text-[17px] font-800 text-gray-900 mb-2 leading-snug">
              {step.title}
            </h3>
            <p className="text-[14px] text-gray-500 leading-relaxed">
              {step.description}
            </p>
          </div>

          {/* Step dots */}
          <div className="px-6 pb-2 flex items-center gap-1.5">
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: i === currentStep ? '20px' : '6px',
                  backgroundColor: i === currentStep ? appPrimary : i < currentStep ? `${appPrimary}50` : '#e5e7eb',
                }}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 pt-2 flex items-center gap-3">
            {!isFirst && (
              <button
                onClick={prevStep}
                className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-arrow-left-line text-sm" />
                Anterior
              </button>
            )}

            <button
              onClick={skipTour}
              className="text-sm text-gray-400 hover:text-gray-600 font-500 cursor-pointer whitespace-nowrap transition-colors px-2"
            >
              {isLast ? '' : 'Pular tudo'}
            </button>

            <button
              onClick={nextStep}
              className="ml-auto flex items-center gap-2 px-5 py-2.5 text-white text-sm font-700 rounded-xl cursor-pointer whitespace-nowrap transition-all active:scale-95"
              style={{ backgroundColor: appPrimary }}
            >
              {isLast ? (
                <><i className="ri-check-line" />Concluir</>
              ) : (
                <>Próximo<i className="ri-arrow-right-line text-sm" /></>
              )}
            </button>
          </div>
        </div>

        {/* Keyboard hint */}
        <p className="text-center text-[11px] text-white/50 mt-3">
          Pressione <kbd className="bg-white/20 rounded px-1 text-white text-[10px]">ESC</kbd> para fechar ou clique fora
        </p>
      </div>
    </>
  );
};

// ─── Keyboard shortcut handler ────────────────────────────────────────────────
// Hook to handle ESC key closing the tour
export const useTourKeyboard = () => {
  const { tourActive, skipTour, nextStep } = useOnboardingTour();

  useEffect(() => {
    if (!tourActive) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skipTour();
      if (e.key === 'ArrowRight' || e.key === 'Enter') nextStep();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [tourActive, skipTour, nextStep]);
};
